import displayError from '@/api/errors/errorResponse'
import {
  createAdvertisementJobService,
  IAdvertisementResponse
} from '@/api/services/advertisements/advertisementsService'

import PageWrap from '@/components/layout/containers/PageWrap'
import Footer from '@/components/layout/footer/Footer'
import Header from '@/components/layout/header/Header'
import { Seo } from '@/components/specific/seo/Seo'
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import GeneratorForm from './_components/GeneratorForm'
import { FormSchema } from './_components/generatorFormSchema'
import ResultPopup from './_components/ResultPopup'

function Generator() {
  const [resultIds, setResultIds] = useState<string[]>([])
  const [generatedIds, setGeneratedIds] = useState<string[]>([])
  const [lastFormData, setLastFormData] = useState<FormSchema | null>(null)
  const [jobData, setJobData] = useState<IAdvertisementResponse['job'] | null>(null)
  const [jobsToGenerate, setJobsToGenerate] = useState<FormSchema[]>([])
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { state }: { state: { generateData?: FormSchema; data?: FormSchema } } =
    useLocation()
  const navigate = useNavigate()

  const {
    mutateAsync: handleCreateJob,
    mutate,
    isPending: jobPending
  } = useMutation({
    mutationFn: (data: FormSchema) => createAdvertisementJobService(data),
    onSuccess: (res) => {
      if (!res.success) return displayError(res)
      setResultIds((prev) => [...prev, ...res.data.advertisementIds])
      setGeneratedIds((prev) => [...prev, ...res.data.advertisementIds])
    }
  })

  const onSubmit = async (data: FormSchema) => {
    setGeneratedIds([])
    setResultIds([])
    setJobData(null)
    setLastFormData(data)
    const mappedData = data.resolution.map((item) => ({
      ...data,
      resolution: item,
      ...(jobData?.shopLogoFileId ? { shopLogoFileTmp: jobData.shopLogoFileId } : {}),
      ...(jobData?.templateFileId ? { templateFileTmp: jobData.templateFileId } : {}),
      ...(jobData?.productImageFileId
        ? { productImageFileTmp: jobData.productImageFileId }
        : {})
    }))
    if (mappedData.length > 0) {
      const firstJob: unknown = mappedData[0]
      await handleCreateJob(firstJob as FormSchema)
      const restJobs: unknown = mappedData.slice(1)
      setJobsToGenerate(restJobs as FormSchema[])
    } else {
      setJobsToGenerate([])
    }
  }

  useEffect(() => {
    if (state?.generateData) {
      mutate(state.generateData)
      navigate(location.pathname, { replace: true })
    }
  }, [state])

  useEffect(() => {
    if (jobData?.id && jobsToGenerate.length > 0) {
      const mappedData = jobsToGenerate.map((item) => ({
        ...item,
        ...(jobData?.shopLogoFileId ? { shopLogoFileTmp: jobData.shopLogoFileId } : {}),
        ...(jobData?.templateFileId ? { templateFileTmp: jobData.templateFileId } : {}),
        ...(jobData?.productImageFileId
          ? { productImageFileTmp: jobData.productImageFileId }
          : {})
      }))
      void Promise.all(mappedData.map((item) => handleCreateJob(item)))
      setJobsToGenerate([])
    }
  }, [jobsToGenerate, jobData])

  return (
    <>
      <Seo />
      <Header />
      <PageWrap className="justify-normal px-2 pt-28 md:px-0">
        <GeneratorForm
          data={state?.data}
          jobData={jobData}
          jobPending={jobPending}
          onSubmit={onSubmit}
          openResultPopup={() => setResultIds(generatedIds)}
          resultIds={generatedIds}
          setJobData={setJobData}
        />
      </PageWrap>
      <Footer />
      {!!resultIds.length && (
        <ResultPopup
          jobData={jobData}
          onSubmit={(data: Partial<FormSchema>) =>
            onSubmit({ ...lastFormData!, ...data })
          }
          resultIds={resultIds}
          setJobData={setJobData}
          setResultIds={setResultIds}
        />
      )}
    </>
  )
}

export default Generator
